import { forwardRef, useCallback, useImperativeHandle, useMemo } from "react";
import { Box, Divider, Stack } from "@mzc-pdc/ui";
import { Typography } from "@components_v2/typography/typography";
import { RandomColoredAvatar } from "@components_v2/avatar/random-colored-avatar";
import { NotifyBadge } from "./notify-badge";
import theme from "@styles/theme";
import moment from "moment";
import { IconLock, IconProjects, IconPublic } from "@mzc-cloudplex/icons";
import { useTranslation } from "react-i18next";
import AssetTypeFlag from "@components_v2/icon-type/asset/asset-type-flag";
import EllipsisTooltip from "@components_v2/ellipsis-tooltip";
import ContentTypeFlag from "@components_v2/icon-type/content/content-type-flag";
import { CONTENT_TYPE, CUSTOM_MENU_DOMAIN, CUSTOM_MENU_TYPE } from "@constants";
import CollectionTypeFlag from "@components_v2/icon-type/asset/collection-type-flag";
import RequestTypeFlag from "@components_v2/icon-type/request/request-type-flag";
import menuService from "@services/menuService";
import { siteMode } from "@cores/siteMode";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { resetCommentLinkState as resetCommonCommentLinkState } from "@modules/common-comments";
import { resetCommentLinkState } from "@modules/comments";

import useUserDetail from "@hooks/useUserDetail";
import UserDetailPopper, { UserDetailButton } from "@features/user-detail-popper/user-detail-popper";

export const NotifyCommentItem = forwardRef(({ item }, ref) => {
    const { t } = useTranslation();
    const { pathname } = useLocation();
    const history = useHistory();
    const dispatch = useDispatch();

    const {
        handleUserProfileClick,
        handleUserProfileClose,
        userProfileAnchorEl,
        openUserProfile,
        userProfileData,
        userProfilePending,
    } = useUserDetail();

    const { spaceId, projectId } = menuService.parse(pathname);
    const isLibrary = siteMode.get() === "CMS";
    const user = useSelector(({ stage }) => stage?.user);
    const settings = useSelector(({ settings }) => settings);
    const customMenus = useSelector(({ customMenus }) => customMenus);

    const isCustomMenu = useMemo(() => {
        return settings?.project?.MenuName?.selectMenu?.value === "custom";
    }, [settings?.project?.MenuName?.selectMenu?.value]);

    const userInfo = useMemo(() => {
        if (item?.data?.type === "INVITED") {
            return item?.data?.resource?.invited?.by;
        }
        return item?.data?.resource?.comment?.created?.by;
    }, [item]);

    const commentMessage = useMemo(() => {
        let messageKey = "";
        switch (item?.data?.type) {
            case "MENTIONED":
                messageKey = "mentioned you in a comment";
                break;
            case "REPLIED":
                messageKey = "replied to your comment";
                break;
            case "INVITED":
                messageKey = "invited you to a private comment";
                break;
            default:
                return null;
        }

        return (
            <>
                <UserDetailButton
                    maxWidth={120}
                    onClick={(e) => handleUserProfileClick(e, userInfo?.id)}
                    userName={
                        <Typography variant="body1_700" color={theme.palette.greySecondary.main}>
                            {userInfo?.name}
                        </Typography>
                    }
                />
                <Typography variant="body2_400" color={theme.palette.greySecondary.main}>
                    {t(`common::msg::${messageKey}`, messageKey)}
                </Typography>
            </>
        );
    }, [item, userInfo, t, handleUserProfileClick]);

    const formattedDate = useMemo(() => {
        if (!item?.receivedAt) return "";
        return moment(item.receivedAt).format("YYYY-MM-DD HH:mm");
    }, [item]);

    const commentDomain = useMemo(() => {
        const context = item?.data?.resource?.thread?.context;
        if (!context) return null;

        if (context.domain === "COMMENT" && context.parentThread) {
            return context.parentThread.context.domain;
        }
        return context.domain;
    }, [item]);

    const commentResourceFlag = useMemo(() => {
        switch (commentDomain) {
            case "REQUEST":
                return <RequestTypeFlag size={"small"} />;
            case "ASSET": {
                return <AssetTypeFlag size={"small"} type={"GENERAL"} />;
            }
            case "COLLECTION":
                return <CollectionTypeFlag size={"small"} />;
            case "CUSTOM_CONTENT":
                return <ContentTypeFlag size={"small"} type={CONTENT_TYPE.CUSTOM} />;
            case "PROJECT":
            default:
                return null;
        }
    }, [commentDomain]);

    const commentResourceName = useMemo(() => {
        const context = item?.data?.resource?.thread?.context;
        if (!context) return "";

        const { domain, metadata, parentThread } = context;

        switch (domain) {
            case "REQUEST":
                return metadata?.title || "";

            case "COMMENT":
                if (parentThread?.context?.domain === "REQUEST") {
                    return parentThread?.context?.metadata?.title || "";
                }
                return parentThread?.context?.metadata?.name || "";

            default:
                return metadata?.name || "";
        }
    }, [item]);

    const isThreadPublic = useMemo(() => item?.data?.resource?.thread?.type === "PUBLIC", [item]);

    const threadName = useMemo(() => {
        const thread = item?.data?.resource?.thread;

        if (isThreadPublic) {
            return t("common::label::All Public", "All Public");
        }

        const context = thread?.context;
        if (context?.domain === "COMMENT") {
            return context?.parentThread?.name || "";
        }
        return thread?.name || "";
    }, [item, t, isThreadPublic]);

    const userSiteAccess = useMemo(
        () => user?.projects?.find((project) => project.id === projectId)?.siteAccess,
        [user, projectId],
    );

    const getDestinationInfo = useCallback(
        (notifyItem) => {
            if (!notifyItem?.data?.resource) {
                return { paths: menuService.paths, isLibrary: false, destinationThread: null, isSubThread: false };
            }

            const thread = notifyItem.data.resource.thread;
            const context = thread?.context;

            const isSubThread = context?.domain === "COMMENT";
            const destinationThread = isSubThread ? context?.parentThread?.context : context;

            if (!destinationThread) {
                return { paths: menuService.paths, isLibrary: false, destinationThread: null, isSubThread };
            }

            let isLibraryPath = isLibrary;
            if (destinationThread.domain === "REQUEST") {
                const isMyRequest = destinationThread?.metadata?.created?.by?.id === user?.id;
                isLibraryPath = isMyRequest && isLibrary;
            }

            const paths = isLibraryPath ? menuService.paths.library : menuService.paths;

            return { paths, isLibrary: isLibraryPath, destinationThread, isSubThread };
        },
        [user, isLibrary],
    );

    const getCommentDestinationPage = useCallback(
        (notifyItem, { paths: menuServicePaths, destinationThread, isSubThread }) => {
            if (!notifyItem?.data?.resource || !destinationThread) return "";

            const thread = notifyItem.data.resource.thread;
            const comment = notifyItem.data.resource.comment;
            const destinationDomain = destinationThread.domain;

            // 현재 프로젝트인지 확인
            const isCurrentProject = notifyItem.data.project?.id === projectId;
            const targetProjectId = notifyItem.data.project?.id;

            let domainPath = "";
            let customMenuDomain = "";

            switch (destinationThread.domain) {
                case "ASSET":
                    if (isCurrentProject && isCustomMenu) {
                        domainPath = `${menuServicePaths.customMenuSets.assetsAssetDetail?.(
                            spaceId,
                            targetProjectId,
                            destinationThread.resourceId,
                        )}?projectId=${targetProjectId}&`;
                    } else {
                        domainPath = `${menuServicePaths.assetsAssetDetail(
                            spaceId,
                            targetProjectId,
                            destinationThread.resourceId,
                        )}?projectId=${targetProjectId}&`;
                        customMenuDomain = CUSTOM_MENU_DOMAIN.ASSETS;
                    }
                    break;
                case "COLLECTION":
                    if (isCurrentProject && isCustomMenu) {
                        const findCollectionMenu = (menus, resourceId) => {
                            for (let menu of menus) {
                                if (menu.domain === CUSTOM_MENU_DOMAIN.COLLECTIONS) {
                                    if (
                                        menu.type === CUSTOM_MENU_TYPE.LIST ||
                                        menu.searchParams?.collections?.[0]?.id === resourceId
                                    ) {
                                        //Note: 이동하려는 컬렉션인 경우 상세 페이지가 메뉴로 사용되었을 경우도 있을 수 있어서 resourceId로 동일한 메뉴를 찾아야한다.
                                        return menu;
                                    }
                                }
                                if (menu.subMenus && menu.subMenus.length > 0) {
                                    const result = findCollectionMenu(menu.subMenus, resourceId);
                                    if (result) return result;
                                }
                            }
                            return undefined;
                        };

                        const collectionMenu = findCollectionMenu(customMenus.data, destinationThread.resourceId);
                        domainPath = `${menuServicePaths.customMenuSets.assetsCollectionDetail(
                            spaceId,
                            targetProjectId,
                            customMenus.setId,
                            collectionMenu?.id || "",
                            destinationThread.resourceId,
                        )}?`;
                    } else {
                        domainPath = `${menuServicePaths.assetsCollectionDetail(
                            spaceId,
                            targetProjectId,
                            destinationThread.resourceId,
                        )}?`;
                        customMenuDomain = CUSTOM_MENU_DOMAIN.COLLECTIONS;
                    }
                    break;
                case "CUSTOM_CONTENT": {
                    const schemaId = destinationThread.resourceId.split(":")[0];
                    const contentId = destinationThread.resourceId.split(":")[1];
                    domainPath = `${menuServicePaths.contentsCustomContentDetail?.(
                        spaceId,
                        targetProjectId,
                        schemaId,
                        contentId,
                    )}?`;
                    break;
                }
                case "PROJECT":
                    if (isCurrentProject && isCustomMenu) {
                        domainPath = `${pathname}?`;
                    } else {
                        domainPath = `${menuServicePaths.dashboard(spaceId, targetProjectId)}?`;
                        customMenuDomain = "PROJECT";
                    }
                    break;
                case "REQUEST": {
                    const isMyRequest = destinationThread?.metadata?.created?.by?.id === user?.id;
                    if (isLibrary && !isMyRequest && !userSiteAccess?.includes("CONSOLE")) return "";
                    domainPath = `${menuServicePaths.requestDetail(
                        spaceId,
                        targetProjectId,
                        destinationThread.resourceId,
                    )}?requestProjectId=${targetProjectId}&`;
                    break;
                }
                default:
                    return "";
            }

            return `${domainPath}threadId=${thread?.id || ""}&commentId=${comment?.id || ""}&isSubThread=${
                isSubThread ? "true" : "false"
            }&domain=${destinationDomain}&linkFromNoti=true&customMenuDomain=${customMenuDomain}`;
        },
        [spaceId, projectId, userSiteAccess, isLibrary, user?.id, isCustomMenu, pathname, customMenus],
    );

    const destinationPageUrl = useMemo(() => {
        const info = getDestinationInfo(item);
        return getCommentDestinationPage(item, info);
    }, [item, getDestinationInfo, getCommentDestinationPage]);

    // 부모에서 호출할 수 있는 메서드 노출
    useImperativeHandle(
        ref,
        () => ({
            handleClick: () => {
                if (destinationPageUrl) {
                    const itemDomain = getDestinationInfo(item)?.destinationThread?.domain;
                    if (itemDomain === "PROJECT") {
                        dispatch(resetCommonCommentLinkState());
                    } else {
                        dispatch(resetCommentLinkState());
                    }

                    if (itemDomain === "REQUEST" || item.data.project?.id === projectId) {
                        //Note: Request는 Space 단위의 페이지여서 타프로젝트로 이동할 필요없이 표시가 가능
                        history.push(destinationPageUrl);
                    } else {
                        window.location.href = destinationPageUrl;
                    }
                }
            },
        }),
        [destinationPageUrl, history, item, projectId, dispatch, getDestinationInfo],
    );

    return (
        <Stack direction="row" gap={theme.spacing(1.5)} sx={{ flex: 1 }}>
            <Box sx={{ flexShrink: 0 }}>
                <RandomColoredAvatar id={userInfo?.id} name={userInfo?.name} size={32} />
            </Box>

            <Stack direction="column" gap={theme.spacing(1)} sx={{ flex: 1 }}>
                <Stack direction="row" justifyContent="space-between" gap={theme.spacing(1)}>
                    <Stack direction="row" gap={theme.spacing(0.5)} sx={{ alignItems: "center" }}>
                        {commentMessage}
                    </Stack>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Typography variant="body4_500" color={theme.palette.greySecondary[500]}>
                            {formattedDate}
                        </Typography>
                    </Box>
                </Stack>

                <Stack direction="row" alignItems="center" gap={theme.spacing(1)} mb={theme.spacing(0.5)}>
                    <Box
                        sx={{
                            flexShrink: 0,
                            color: theme.palette.secondary[400],
                        }}
                    >
                        <IconProjects size={12} />
                    </Box>
                    <Typography variant="body4_500" color={theme.palette.secondary[400]}>
                        {item?.data?.project?.name || "-"}
                    </Typography>
                </Stack>

                <Stack direction="row" alignItems="center" gap={theme.spacing(1)}>
                    {commentDomain && (
                        <Box sx={{ flexShrink: 0, display: "flex", alignItems: "center" }}>
                            <NotifyBadge domain={commentDomain} />
                        </Box>
                    )}
                    <Stack direction="row" alignItems="center">
                        <Box sx={{ display: "flex", alignItems: "center" }}>{commentResourceFlag}</Box>
                        <EllipsisTooltip
                            text={commentResourceName}
                            variant="body3_500"
                            maxWidth={"266px"}
                            typographyProps={{ color: theme.palette.primary.main }}
                        />
                    </Stack>
                    <Divider orientation="vertical" flexItem color={theme.palette.greySecondary[100]} />
                    <Stack direction="row" alignItems="center" gap={theme.spacing(0.5)} sx={{ flexShrink: 0 }}>
                        <Box sx={{ color: theme.palette.greySecondary[500], display: "flex", alignItems: "center" }}>
                            {isThreadPublic ? <IconPublic size={16} /> : <IconLock size={16} />}
                        </Box>
                        <Box>
                            <EllipsisTooltip
                                text={threadName}
                                variant="body3_500"
                                maxWidth={"67px"}
                                typographyProps={{ color: theme.palette.greySecondary[500] }}
                            />
                        </Box>
                    </Stack>
                </Stack>
            </Stack>
            {!userProfilePending && (
                <UserDetailPopper
                    id={openUserProfile ? "user-profile-popover" : undefined}
                    handleUserProfileClose={handleUserProfileClose}
                    userProfileAnchorEl={userProfileAnchorEl}
                    openUserProfile={openUserProfile}
                    userProfileData={userProfileData}
                />
            )}
        </Stack>
    );
});

NotifyCommentItem.displayName = "NotifyCommentItem";
